import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { FiCopy, FiServer, FiMessageSquare, FiLink } from 'react-icons/fi';
import api from '../../../api/api';
import { McpConnectedApp } from '../../../model/McpConnectedApps';
import { toast } from '../Toast';
import { copyText } from '../clientSettings/widgets/shared';
import AccountNav from './AccountNav';

// one Streamable HTTP endpoint for every client, OAuth only (doc/mcp/spec.md); dev box serves it on dev-mcp.
const MCP_URL = window.location.hostname.startsWith('dev') ? 'https://dev-mcp.investi.com.au/mcp' : 'https://mcp.investi.com.au/mcp';
const CLAUDE_CODE_CMD = `claude mcp add --transport http --scope user investi ${MCP_URL}`;

type App = 'claude' | 'chatgpt' | 'claudeCode';
const APPS: { key: App; label: string }[] = [
    { key: 'claude', label: 'Claude' },
    { key: 'chatgpt', label: 'ChatGPT' },
    { key: 'claudeCode', label: 'Claude Code' },
];

const TOOLS = ['get_stock_config'];

const PROMPTS = [
    'Show me our Investi settings',
    'Auto send price sensitive announcements, but manually confirm the others',
    'How did our last email campaign perform? Who opened it, who clicked, and did anyone unsubscribe?',
    'What were the most asked questions after our last announcement?',
];

const Code: React.FC<{ text: string; what: string }> = ({ text, what }) =>
    <div className="mcp-code">
        <code>{text}</code>
        <button type="button" className="btn-outline with-icon" onClick={() => copyText(text, what)}><FiCopy /> Copy</button>
    </div>;

const Steps: React.FC<{ app: App }> = ({ app }) => {
    if (app === 'claude') {
        return <>
            <p className="hint">Works across Claude web, desktop and mobile once added.</p>
            <ol className="mcp-steps">
                <li>In Claude, go to <strong>Settings → Connectors → Add custom connector</strong></li>
                <li>Set <strong>Name</strong> to <code>Investi</code></li>
                <li>Set <strong>Remote MCP server URL</strong> to:<Code text={MCP_URL} what="server URL" /></li>
                <li>Click <strong>Connect</strong>, sign in with your Investi login, and approve access.</li>
            </ol>
        </>;
    }
    if (app === 'chatgpt') {
        return <>
            <p className="hint">Custom connectors need developer mode on some ChatGPT plans.</p>
            <ol className="mcp-steps">
                <li>In ChatGPT, go to <strong>Settings → Connectors → Create</strong></li>
                <li>Set <strong>Name</strong> to <code>Investi</code></li>
                <li>Set <strong>MCP server URL</strong> to:<Code text={MCP_URL} what="server URL" /></li>
                <li>Choose <strong>OAuth</strong> authentication, click <strong>Create</strong>, then sign in with your Investi login and approve access.</li>
            </ol>
        </>;
    }
    return <ol className="mcp-steps">
        <li>Run this in a terminal:<Code text={CLAUDE_CODE_CMD} what="command" /></li>
        <li>In Claude Code, type <code>/mcp</code>, choose <strong>investi</strong>, then sign in with your Investi login and approve access.</li>
    </ol>;
};

const McpServer: React.FC = () => {
    const [app, setApp] = useState<App>('claude');
    const [apps, setApps] = useState<McpConnectedApp[] | null>(null);

    useEffect(() => {
        api().getMcpConnectedApps().then(r => setApps(r.apps)).catch(() => setApps([]));
    }, []);

    // revoking stops refreshes; the app's current access token still works until it expires (up to an hour)
    const revoke = async (a: McpConnectedApp) => {
        try {
            await api().revokeMcpConnectedApp(a.id);
            setApps(x => (x ?? []).filter(y => y.id !== a.id));
            toast.success(`Revoked ${a.clientName}`);
        } catch {
            toast.error('Could not revoke, please try again');
        }
    };

    return <>
        <div className="page-header">
            <div>
                <h1>MCP Server</h1>
                <p>Connect your own AI assistant to Investi so it can work with your company’s data.</p>
            </div>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main mcp-server">
                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiServer /></span><h2>Connect Your AI App</h2></div>
                    <div className="mcp-tabs" role="tablist">
                        {APPS.map(a => <button key={a.key} type="button" role="tab" aria-selected={a.key === app}
                            className={a.key === app ? 'active' : ''} onClick={() => setApp(a.key)}>{a.label}</button>)}
                    </div>
                    <Steps app={app} />
                    <p className="hint">You can view and revoke access at any time under Connected Apps.</p>
                    <div className="mcp-tools">
                        <div className="title">{TOOLS.length} {TOOLS.length === 1 ? 'tool' : 'tools'} available</div>
                        <ul>{TOOLS.map(t => <li key={t}><code>{t}</code></li>)}</ul>
                    </div>
                </section>

                <div className="mcp-side">
                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiMessageSquare /></span><h2>Example Prompts</h2></div>
                        <ul className="mcp-prompts">{PROMPTS.map(p => <li key={p}>“{p}”</li>)}</ul>
                    </section>

                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiLink /></span><h2>Connected Apps</h2></div>
                        {apps !== null && apps.length === 0 && <p className="hint">No apps connected yet. Apps you approve will appear here, and you can revoke them at any time.</p>}
                        {apps?.map(a => <div key={a.id} className="setting-row">
                            <div className="text">
                                <div className="title">{a.clientName}</div>
                                <div className="hint">Connected {moment(a.created).format('D MMM YYYY')} · last used {moment(a.lastUsed).fromNow()}</div>
                            </div>
                            <button type="button" className="link" onClick={() => revoke(a)}>Revoke</button>
                        </div>)}
                    </section>
                </div>
            </div>
        </div>
    </>;
};

export default McpServer;
